import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, NgModule } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpClientModule, HttpClientXsrfModule, HttpEventType, HttpParams, HttpUrlEncodingCodec, HttpContext, HttpHeaders } from '@angular/common/http';
import { AboutApi, SystemPropertiesApi, DiscoveryApi, isBrowser } from '@alfresco/js-api';
import { SHOULD_ADD_AUTH_TOKEN } from '@alfresco/adf-core/auth';
import { Subject, of, throwError } from 'rxjs';
import { map, catchError, takeUntil } from 'rxjs/operators';

/*!
 * @license
 * Copyright © 2005-2023 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const API_CLIENT_FACTORY_TOKEN = new InjectionToken('api-client-factory');

/*!
 * @license
 * Copyright © 2005-2023 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/* eslint-enable */
class ApiClientsService {
    constructor(apiCreateFactory) {
        this.apiCreateFactory = apiCreateFactory;
        this.registry = {};
        this.instances = {};
    }
    get(apiName) {
        const apiClass = this.registry[apiName];
        if (!apiClass) {
            throw new Error(`Api not registred: ${apiName}`);
        }
        return this.instances[apiName] ?? this.instantiateApi(apiName);
    }
    register(apiName, api) {
        this.registry[apiName] = api;
    }
    instantiateApi(apiName) {
        const apiClass = this.registry[apiName];
        const instance = this.apiCreateFactory.create(apiClass);
        this.instances[apiName] = instance;
        return instance;
    }
}
ApiClientsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: ApiClientsService, deps: [{ token: API_CLIENT_FACTORY_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable });
ApiClientsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: ApiClientsService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: ApiClientsService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [API_CLIENT_FACTORY_TOKEN]
                }] }]; } });

/*!
 * @license
 * Copyright © 2005-2023 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*!
 * @license
 * Copyright © 2005-2023 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class ActivitiClientModule {
    constructor(apiClientsService) {
        this.apiClientsService = apiClientsService;
        this.apiClientsService.register('ActivitiClient.about', AboutApi);
        this.apiClientsService.register('ActivitiClient.system-properties', SystemPropertiesApi);
    }
}
ActivitiClientModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: ActivitiClientModule, deps: [{ token: ApiClientsService }], target: i0.ɵɵFactoryTarget.NgModule });
ActivitiClientModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.1.3", ngImport: i0, type: ActivitiClientModule });
ActivitiClientModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: ActivitiClientModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: ActivitiClientModule, decorators: [{
            type: NgModule
        }], ctorParameters: function () { return [{ type: ApiClientsService }]; } });

/*!
 * @license
 * Copyright © 2005-2023 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class DiscoveryClientModule {
    constructor(apiClientsService) {
        this.apiClientsService = apiClientsService;
        this.apiClientsService.register('DiscoveryClient.discovery', DiscoveryApi);
    }
}
DiscoveryClientModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: DiscoveryClientModule, deps: [{ token: ApiClientsService }], target: i0.ɵɵFactoryTarget.NgModule });
DiscoveryClientModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.1.3", ngImport: i0, type: DiscoveryClientModule });
DiscoveryClientModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: DiscoveryClientModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: DiscoveryClientModule, decorators: [{
            type: NgModule
        }], ctorParameters: function () { return [{ type: ApiClientsService }]; } });

/*!
 * @license
 * Copyright © 2005-2023 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class AlfrescoJsClientsModule {
}
AlfrescoJsClientsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: AlfrescoJsClientsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AlfrescoJsClientsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.1.3", ngImport: i0, type: AlfrescoJsClientsModule, imports: [HttpClientModule, i1.HttpClientXsrfModule, ActivitiClientModule,
        DiscoveryClientModule] });
AlfrescoJsClientsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: AlfrescoJsClientsModule, providers: [
        ApiClientsService
    ], imports: [HttpClientModule,
        HttpClientXsrfModule.withOptions({
            cookieName: 'CSRF-TOKEN',
            headerName: 'X-CSRF-TOKEN'
        }),
        ActivitiClientModule,
        DiscoveryClientModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: AlfrescoJsClientsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        HttpClientModule,
                        HttpClientXsrfModule.withOptions({
                            cookieName: 'CSRF-TOKEN',
                            headerName: 'X-CSRF-TOKEN'
                        }),
                        ActivitiClientModule,
                        DiscoveryClientModule
                    ],
                    providers: [
                        ApiClientsService
                    ]
                }]
        }] });

/*!
 * @license
 * Copyright © 2005-2023 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*!
 * @license
 * Copyright © 2005-2023 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*!
 * @license
 * Copyright © 2005-2023 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
;

/*!
 * @license
 * Copyright © 2005-2023 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const isHttpUploadProgressEvent = (val) => val.type === HttpEventType.UploadProgress;
const isHttpResponseEvent = (val) => val.type === HttpEventType.Response;
const isDate = (value) => value instanceof Date;
const isXML = (value) => typeof value === 'string' && value.startsWith('<?xml');
const isBlobResponse = (response, returnType) => returnType === 'blob' || response.body instanceof Blob;
const isConstructor = (value) => typeof value === 'function' && !!value?.prototype?.constructor.name;
const convertParamsToString = (value) => isDate(value) ? value.toISOString() : value;
const getQueryParamsWithCustomEncoder = (obj, encoder = new HttpUrlEncodingCodec()) => {
    if (!obj) {
        return undefined;
    }
    let httpParams = new HttpParams({
        encoder
    });
    const params = removeNilValues(obj);
    for (const key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
            const value = params[key];
            if (value instanceof Array) {
                const array = value.map(convertParamsToString).filter(Boolean);
                httpParams = httpParams.appendAll({
                    [key]: array
                });
            }
            else {
                httpParams = httpParams.append(key, convertParamsToString(value));
            }
        }
    }
    return httpParams;
};
/**
 * Removes null and undefined values from an object.
 */
const removeNilValues = (obj) => {
    if (!obj) {
        return {};
    }
    return Object.keys(obj).reduce((acc, key) => {
        const value = obj[key];
        const isNil = value === undefined || value === null;
        return isNil ? acc : { ...acc, [key]: value };
    }, {});
};
const convertObjectToFormData = (formParams) => {
    const formData = new FormData();
    for (const key in formParams) {
        if (Object.prototype.hasOwnProperty.call(formParams, key)) {
            const value = formParams[key];
            if (value instanceof File) {
                formData.append(key, value, value.name);
            }
            else {
                formData.append(key, value);
            }
        }
    }
    return formData;
};

/*!
 * @license
 * Copyright © 2005-2023 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// The default implementation of HttpParameterCodec from angular
// does not encode some special characters like + with is causing issues with the alfresco js API and returns 500 error
class AlfrescoApiParamEncoder {
    encodeKey(key) {
        return encodeURIComponent(key);
    }
    encodeValue(value) {
        return encodeURIComponent(value);
    }
    decodeKey(key) {
        return decodeURIComponent(key);
    }
    decodeValue(value) {
        return decodeURIComponent(value);
    }
}

/*!
 * @license
 * Copyright © 2005-2023 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class AlfrescoApiResponseError extends Error {
    constructor(msg, status, error) {
        super(msg);
        this.status = status;
        this.error = error;
        this.name = 'AlfrescoApiResponseError';
    }
}

/*!
 * @license
 * Copyright © 2005-2023 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class AlfrescoApiHttpClient {
    constructor(httpClient) {
        this.httpClient = httpClient;
    }
    request(url, options, sc, emitters) {
        const body = AlfrescoApiHttpClient.getBody(options);
        const params = getQueryParamsWithCustomEncoder(options.queryParams, new AlfrescoApiParamEncoder());
        const headers = AlfrescoApiHttpClient.getHeaders(options);
        const responseType = AlfrescoApiHttpClient.getResponseType(options);
        const context = new HttpContext().set(SHOULD_ADD_AUTH_TOKEN, true);
        const request = this.httpClient.request(options.httpMethod, url, {
            context,
            ...(body && { body }),
            ...(responseType && { responseType }),
            ...(sc.withCredentials && { withCredentials: true }),
            ...(params && { params }),
            headers,
            observe: 'events',
            reportProgress: true
        });
        return this.requestWithLegacyEventEmitters(request, emitters, options.returnType);
    }
    post(url, options, sc, emitters) {
        return this.request(url, { ...options, httpMethod: 'POST' }, sc, emitters);
    }
    put(url, options, sc, emitters) {
        return this.request(url, { ...options, httpMethod: 'PUT' }, sc, emitters);
    }
    get(url, options, sc, emitters) {
        return this.request(url, { ...options, httpMethod: 'GET' }, sc, emitters);
    }
    delete(url, options, sc, emitters) {
        return this.request(url, { ...options, httpMethod: 'DELETE' }, sc, emitters);
    }
    requestWithLegacyEventEmitters(request$, emitters, returnType) {
        const abort$ = new Subject();
        const { eventEmitter, apiClientEmitter } = emitters;
        const promise = request$.pipe(map((res) => {
            if (isHttpUploadProgressEvent(res)) {
                const percent = Math.round((res.loaded / res.total) * 100);
                eventEmitter.emit('progress', { loaded: res.loaded, total: res.total, percent });
            }
            if (isHttpResponseEvent(res)) {
                eventEmitter.emit('success', res.body);
                return AlfrescoApiHttpClient.deserialize(res, returnType);
            }
        }), catchError((err) => {
            // since we can't always determinate ahead of time if the response is going to be xml or plain text response
            // we need to handle false positive cases here.
            if (err.status === 200) {
                eventEmitter.emit('success', err.error.text);
                return of(err.error.text);
            }
            eventEmitter.emit('error', err);
            apiClientEmitter.emit('error', err);
            if (err.status === 401) {
                eventEmitter.emit('unauthorized');
                apiClientEmitter.emit('unauthorized');
            }
            // for backwards compatibility we need to convert it to error class as the HttpErrorResponse only implements Error interface, not extending it,
            // and we need to be able to correctly pass instanceof Error conditions used inside repository
            // we also need to pass error as Stringify string as we are detecting statusCodes using JSON.parse(error.message) in some places
            const msg = typeof err.error === 'string' ? err.error : JSON.stringify(err.error);
            // for backwards compatibility to handle cases in code where we try read response.error.response.body;
            const error = {
                response: { ...err, body: err.error }
            };
            const alfrescoApiError = new AlfrescoApiResponseError(msg, err.status, error);
            return throwError(alfrescoApiError);
        }), takeUntil(abort$)).toPromise();
        promise.abort = function () {
            eventEmitter.emit('abort');
            abort$.next();
            abort$.complete();
            return this;
        };
        return promise;
    }
    static getBody(options) {
        const contentType = options.contentType;
        const isFormData = contentType === 'multipart/form-data';
        const isFormUrlEncoded = contentType === 'application/x-www-form-urlencoded';
        const body = options.bodyParam;
        if (isFormData) {
            return convertObjectToFormData(options.formParams);
        }
        if (isFormUrlEncoded) {
            return new HttpParams({ fromObject: removeNilValues(options.formParams) });
        }
        return body;
    }
    static getHeaders(options) {
        const optionsHeaders = {
            ...options.headerParams,
            ...(options.accept && { Accept: options.accept }),
            ...((options.contentType) && { 'Content-Type': options.contentType })
        };
        return new HttpHeaders(optionsHeaders);
    }
    static getResponseType(options) {
        const isBlobType = options.returnType?.toString().toLowerCase() === 'blob' || options.responseType?.toString().toLowerCase() === 'blob';
        if (isBlobType) {
            return 'blob';
        }
        if (options.returnType === 'String') {
            return 'text';
        }
        return 'json';
    }
    /**
     * Deserialize an HTTP response body into a value of the specified type.
     */
    static deserialize(response, returnType) {
        if (response === null) {
            return null;
        }
        const body = response.body;
        if (!returnType) {
            // for backwards compatibility we need to return empty string instead of null,
            // to avoid issues when accessing null response would break application [C309878]
            // cannot read property 'entry' of null in cases like
            // return this.post(apiUrl, saveFormRepresentation).pipe(map((res: any) => res.entry))
            return body !== null ? body : '';
        }
        if (isBlobResponse(response, returnType)) {
            return AlfrescoApiHttpClient.deserializeBlobResponse(response);
        }
        if (!isConstructor(returnType)) {
            return body;
        }
        if (Array.isArray(body)) {
            return body.map((element) => new returnType(element));
        }
        return new returnType(body);
    }
    static deserializeBlobResponse(response) {
        if (isBrowser()) {
            return new Blob([response.body], { type: response.headers.get('Content-Type') });
        }
        return Buffer.from(response.body, 'binary');
    }
}
AlfrescoApiHttpClient.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: AlfrescoApiHttpClient, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
AlfrescoApiHttpClient.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: AlfrescoApiHttpClient, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: AlfrescoApiHttpClient, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }]; } });

/*!
 * @license
 * Copyright © 2005-2023 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Generated bundle index. Do not edit.
 */

export { API_CLIENT_FACTORY_TOKEN, AlfrescoApiHttpClient, AlfrescoJsClientsModule, ApiClientsService };
